import React, { useEffect, useRef, useState } from "react";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useTranslation } from "react-i18next";
import { PUBLIC_CURRENCY_OPTIONS } from "./publicBookingCurrency";

interface Props {
  value: string;
  onChange: (nextCurrency: string) => void;
}

const PublicCurrencySwitch = ({ value, onChange }: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const currentCurrency = PUBLIC_CURRENCY_OPTIONS.find((option) => option.code === value) ?? PUBLIC_CURRENCY_OPTIONS[0];

  return (
    <div className="theme-dropdown public-currency-switcher" ref={rootRef}>
      <button
        type="button"
        className="theme-dropdown-trigger public-currency-trigger"
        onClick={() => setIsOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t("menuDetails.currency", { defaultValue: "Currency" })}
      >
        <span>{currentCurrency.code}</span>
        <KeyboardArrowDownRoundedIcon fontSize="inherit" />
      </button>

      {isOpen ? (
        <div className="theme-dropdown-menu public-currency-menu" role="menu">
          {PUBLIC_CURRENCY_OPTIONS.map((option) => {
            const isActive = option.code === currentCurrency.code;

            return (
              <button
                key={option.code}
                type="button"
                className={`theme-dropdown-item public-currency-item ${isActive ? "active" : ""}`}
                onClick={() => {
                  onChange(option.code);
                  setIsOpen(false);
                }}
                role="menuitemradio"
                aria-checked={isActive}
              >
                <span>{option.code}</span>
                <strong>{option.symbol}</strong>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default PublicCurrencySwitch;
